import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';

import { Button } from '../../../../components/Button';
import { EditTool } from '../../UploadDocumentPage/editTool';
import { ImageUpload } from '../../../../components/ImageUpload';
import { InfoMessage } from '../../../../components/InfoMessage';
import { ModalContainer } from '../../../../components/ModalContainer';

import * as api from '../../../../api';
import * as logger from '../../../../logger';
import {
  convertPdfToImage,
  dataURItoBlob,
  FileUpload,
  getDataUriFromFile,
  getUploadClass,
  Labels,
  mergePictures,
  uploadImage,
} from '../../UploadDocumentPage/utils';

import defaultStyle from '../../../../styles/formSetStyles.module.css';
import styles from '../../UploadDocumentPage/index.module.css';

import lockIcon from '../../../../assets/lock-outline-circle.svg';
import { processUpload } from '../../UploadDocumentPage/utils';
import { Side } from '../../../../utils/enum';

interface Props extends WithTranslation {
  label: string; // top page label
  type: string; // type of card we're asking for
  skipLabel: string; // bottom page continue label
  documentType: api.PatientUpload.DocumentType;
  required: boolean;
  uploadParams?: {
    // extra parameters passed in the upload
    primary?: boolean;
  };
  onSkip: () => void;
  onUploadComplete: (documentId: string) => Promise<void>;
  showEditTool?: boolean;
  sendToOcr?: (
    uploadId: string,
    side: Side,
    payor?: string,
  ) => Promise<api.InsuranceCardOcrResult.InsuranceCardOcrResult | undefined>;
  updateInsuranceFootprintValue?: (
    footprint: api.PrevisitPatientInsurance.Footprint,
  ) => Promise<void>;
  encounter: api.Encounter.Encounter;
}

interface State {
  uploads: FileUpload[];
  isUploading: boolean;
  errorMsg?: string;
  uploadProgressClass?: string;
  documentId?: string;
  fileToEdit: {
    file: Blob;
    side: Side;
  } | null;
}

class UploadPage extends React.Component<Props, State> {
  formRef = React.createRef<HTMLFormElement>();

  constructor(props: Props) {
    super(props);
    this.state = {
      uploads: [],
      isUploading: false,
      fileToEdit: null,
    };
  }

  getPreview(side: Side): string | undefined {
    const upload = this.state.uploads.find((upload) => upload.side === side);
    if (upload) {
      return upload.dataUri;
    }
  }

  fileChanged = async (file: Blob, side: Side, isEdited?: boolean) => {
    const { updateInsuranceFootprintValue, showEditTool, t } = this.props;

    if (updateInsuranceFootprintValue) {
      if (side === Side.Front)
        await updateInsuranceFootprintValue(
          api.PrevisitPatientInsurance.Footprint.UploadedFront,
        );
      else if (side === Side.Back)
        await updateInsuranceFootprintValue(
          api.PrevisitPatientInsurance.Footprint.UploadedBack,
        );
    }
    if (!isEdited && showEditTool) {
      if (file.type === 'application/pdf') {
        try {
          const pdfToImage = await convertPdfToImage(file);
          this.setState({ fileToEdit: { file: pdfToImage, side } });
        } catch (e) {
          this.setState({
            errorMsg: t('errorOccuredRetryImage'),
          });
          logger.error(
            e,
            `[UploadDocumentPage][fileChanged] failed to convert PDF to image`,
          );
        }
      } else {
        this.setState({ fileToEdit: { file, side } });
      }
    } else {
      const dataUri = await getDataUriFromFile(file);
      const uploads = this.state.uploads.filter((upload) => {
        return upload && upload.side !== side;
      });
      uploads.push({
        dataUri: dataUri,
        rawExtension: file.type,
        side: side,
        blob: file,
      });
      this.setState((state) => ({
        ...state,
        uploads,
        errorMsg: undefined,
        fileToEdit: null,
      }));
    }
  };

  onUploadProgressHandler = (event: api.AxiosProgressEvent) => {
    if (!event.event.lengthComputable) return;
    const uploadProgressClass = getUploadClass(event.loaded, event.total!);
    this.setState({ uploadProgressClass });
  };

  upload = async (): Promise<void> => {
    const {
      encounter,
      documentType,
      uploadParams,
      sendToOcr,
      onUploadComplete,
      t,
    } = this.props;
    const { uploads } = this.state;

    if (uploads.length !== 2) {
      return this.setState({ uploadProgressClass: undefined });
    }

    this.setState({
      errorMsg: undefined,
      isUploading: true,
      uploadProgressClass: styles.Progress0,
    });

    const resetProgress = () =>
      this.setState({ uploadProgressClass: undefined });

    const onError = (e: Error) => {
      this.setState({
        errorMsg: t('errorOccuredTryAgain'),
        isUploading: false,
      });
      logger.error(e, `[UploadDocumentPage][upload] error`);
      resetProgress();
    };

    let response;
    try {
      response = await api.client.PatientUpload.getSignedUploadURL(
        encounter.id,
        documentType,
        uploadParams,
      );
    } catch (e) {
      return onError(e);
    }
    const { id, url, patientInsuranceInformationId } = response;

    // upload to OCR
    const frontUpload = uploads.find((upload) => upload.side === Side.Front);
    const backUpload = uploads.find((upload) => upload.side === Side.Back);
    if (!frontUpload || !backUpload) {
      return this.setState({ uploadProgressClass: undefined });
    }
    const frontResult =
      sendToOcr &&
      (await uploadImage({
        documentType: api.PatientUpload.DocumentType.OcrVerification,
        imageBlob: frontUpload.blob,
        encounterId: encounter.id,
        onUploaded: (uploadId: string) => sendToOcr(uploadId, Side.Front),
        options: { patientInsuranceInformationId },
      }));

    sendToOcr &&
      (await uploadImage({
        documentType: api.PatientUpload.DocumentType.OcrVerification,
        imageBlob: backUpload.blob,
        encounterId: encounter.id,
        // send payor with back result, if provided
        onUploaded: (uploadId: string) =>
          sendToOcr(uploadId, Side.Back, frontResult?.insuranceProviderName),
        options: { patientInsuranceInformationId },
      }));

    // merge + upload to insurance
    try {
      const merged = await mergePictures({
        frontDataUri: frontUpload.dataUri,
        backDataUri: backUpload.dataUri,
      });

      this.setState({ documentId: id });
      if (url) {
        const mergedBlob = dataURItoBlob(merged);
        const status = await processUpload(
          mergedBlob,
          url,
          this.onUploadProgressHandler,
          (errorMsg: string) => this.setState({ errorMsg }),
        );
        if (status === 200) {
          await api.client.PatientUpload.validateUploadedDocuments(id);
          await onUploadComplete(id);
        }
      }
    } catch (e) {
      return onError(e);
    }
    resetProgress();
  };

  cancelUpload = () => {
    if (this.formRef.current) this.formRef.current.reset();
    this.setState({
      uploads: [],
      errorMsg: '',
    });
  };

  isSubmitDisabled() {
    return this.state.isUploading || this.state.uploads.length === 1;
  }

  renderSubmitButton() {
    const { required, skipLabel, onSkip, t } = this.props;
    const isEmpty: boolean = !this.state.uploads.length;
    if (isEmpty) {
      if (required) return null;
      return (
        <div className={defaultStyle.ButtonSubmitWrapper}>
          <Button
            type="button"
            children={skipLabel}
            onClick={onSkip}
            invertColors={true}
          />
        </div>
      );
    }

    return (
      <div className={defaultStyle.ButtonSubmitWrapper}>
        <Button
          type="button"
          children={
            this.state.uploadProgressClass ? (
              <span className={styles.Spinner}></span>
            ) : (
              t('Upload')
            )
          }
          className={
            this.state.uploadProgressClass
              ? `${styles.Uploading} ${this.state.uploadProgressClass}`
              : ''
          }
          onClick={this.upload}
          invertColors={isEmpty}
          disabled={this.isSubmitDisabled()}
        />
        <Button
          type="button"
          children={t('Clear images')}
          disabled={this.state.isUploading}
          invertColors={true}
          onClick={this.cancelUpload}
        />
      </div>
    );
  }

  render() {
    const { label, type, updateInsuranceFootprintValue, t } = this.props;
    const { uploads, fileToEdit, errorMsg } = this.state;

    if (fileToEdit) {
      const continueLabel =
        fileToEdit.side === Side.Front
          ? t('Save front photo')
          : t('Save back photo');
      const instructions = t(
        `Take a picture of the ${fileToEdit.side.toLowerCase()} of your ${type.toLowerCase()} & position it in the frame. Make sure all four corners of your card are visible.`,
      );
      const topLabel =
        fileToEdit.side === Side.Front ? t('Front of card') : t('Back of card');

      return (
        <ModalContainer
          isOpen
          noPadding
          isDarkMode
          hideCancelButtonOnTablet
          onModalClose={() => {
            this.setState({ fileToEdit: null });
          }}
          topLabel={topLabel}
          contentClassName={styles.UploadModalContainer}
          component={
            <EditTool
              onSave={(picture: Blob) => {
                this.fileChanged(picture, fileToEdit.side, true);
              }}
              continueLabel={continueLabel}
              instructions={instructions}
              file={fileToEdit.file}
            />
          }
        />
      );
    }
    const secureMessage = t('dataIsSecure');

    return (
      <div className={styles.UploadPage}>
        {errorMsg ? <InfoMessage isError={true}>{errorMsg}</InfoMessage> : null}
        <form className={`${styles.CardUploadPage}`}>
          <div className={styles.Title} role={'heading'} aria-level={1}>
            {label}
          </div>
          {!uploads.length && (
            <div className={styles.SecureMessage}>
              <img
                className={styles.LockIcon}
                src={lockIcon}
                alt="lock"
                aria-hidden="true"
              />
              <p>{secureMessage}</p>
            </div>
          )}
          <ImageUpload
            label={`${Labels.imageUploadFront} ${type}`}
            side={Side.Front}
            fileChanged={this.fileChanged}
            preview={this.getPreview(Side.Front)}
            handleUploadClick={async () =>
              updateInsuranceFootprintValue &&
              (await updateInsuranceFootprintValue(
                api.PrevisitPatientInsurance.Footprint.PressedFrontPhotoIcon,
              ))
            }
          />
          <ImageUpload
            label={`${Labels.imageUploadBack} ${type}`}
            side={Side.Back}
            fileChanged={this.fileChanged}
            preview={this.getPreview(Side.Back)}
            handleUploadClick={async () =>
              updateInsuranceFootprintValue &&
              (await updateInsuranceFootprintValue(
                api.PrevisitPatientInsurance.Footprint.PressedBackPhotoIcon,
              ))
            }
          />
          {this.renderSubmitButton()}
        </form>
      </div>
    );
  }
}

export default withTranslation()(UploadPage);
